import type {
  UseComboboxStateChange,
  UseMultipleSelectionStateChange,
} from 'downshift';
import {useCombobox, useMultipleSelection} from 'downshift';
import {useCallback, useMemo, useRef, useState} from 'react';
import type {KeyboardEvent, PropsWithChildren} from 'react';

import {checkEquality} from '#lit/utils/arrays.js';
import {fuzzyMatch} from '#utils/strings.js';

import {Flex} from '../../../atoms/flex/index.js';
import {MultiComboBoxContext} from '../context.js';
import type {
  MultiComboBoxContextType,
  MultiComboBoxInfiniteLoader,
} from '../context.js';
import {itemToStringDefault} from '../utils.js';
import {Anchor} from './anchor.js';
import {Items} from './items.js';
import {Search} from './search.js';
import {SelectedItems} from './selected_items.js';
import {Surface, SurfaceLoadingIndicator} from './surface.js';
import {ToggleButton} from './toggle_button.js';

type LoadItems<T> = (
  inputValue: string
) => Promise<{items: T[]; hasMore: boolean}>;

type MultiComboBoxBaseProps<T> = {
  itemToString?: (item: T | null) => string;
  initialSelectedItems?: T[];
  onChange?: (selectedItems: T[]) => void;
  allowCustomItems?: boolean;
  clearable?: boolean;
  asChild?: boolean;
  as?: 'MultiSelect' | 'ComboBox';
  isItemDisabled?: (item: T) => boolean;
  anchorPlaceholder?: string;
  toggleAriaLabel?: string;
  emptyStateMessage?: string;
};

type MultiComboBoxItemsProps<T> = MultiComboBoxBaseProps<T> & {
  items?: T[];
  loadItems?: never;
  isLoading?: boolean;
};

type MultiComboBoxLoadItemsProps<T> = MultiComboBoxBaseProps<T> & {
  items?: never;
  loadItems: LoadItems<T>;
  isLoading?: never;
};

/**
 * Pass `items` for a local list or `loadItems` for async paging, never both.
 * `loadItems` owns the list and loading; `isLoading` is only valid with `items`.
 */
type MultiComboBoxProps<T> =
  | MultiComboBoxItemsProps<T>
  | MultiComboBoxLoadItemsProps<T>;

const Root = <T,>({
  as,
  allowCustomItems = true,
  clearable = true,
  asChild = false,
  children,
  initialSelectedItems,
  items,
  itemToString = itemToStringDefault,
  loadItems: loadItemsProp,
  isLoading: isLoadingProp,
  onChange,
  isItemDisabled,
  anchorPlaceholder,
  toggleAriaLabel,
  emptyStateMessage = 'No items found',
}: PropsWithChildren<MultiComboBoxProps<T>>) => {
  const customItems = useRef(new Set<T>());
  const customItemsList = Array.from(customItems.current).sort();
  const initialLoadedItems = useRef<T[]>(items ?? []);
  const hasAttemptedInitialLoadRef = useRef(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState('');
  const infiniteLoaderRef = useRef<MultiComboBoxInfiniteLoader>(null);
  const [hasExternalInfiniteLoader, setHasExternalInfiniteLoader] =
    useState(false);
  const [hasMore, setHasMore] = useState(false);
  const registerInfiniteLoader = useCallback(
    (loader: MultiComboBoxInfiniteLoader | null) => {
      infiniteLoaderRef.current = loader;
      setHasExternalInfiniteLoader(Boolean(loader));
      if (loader) {
        setHasMore(true);
      }
    },
    []
  );
  const [internalIsLoading, setInternalIsLoading] = useState(false);
  const [itemsList, setItemsList] = useState<T[]>(items ?? []);
  const itemsListRef = useRef<T[]>(itemsList);
  itemsListRef.current = itemsList;
  const [selectedItems, setSelectedItems] = useState<T[]>(
    initialSelectedItems
      ? as === 'ComboBox'
        ? initialSelectedItems.slice(0, 1)
        : initialSelectedItems
      : []
  );

  const doSelectedItems = useMemo(() => {
    return (nextItems: T[]) => {
      const usedNextItems = as === 'ComboBox' ? nextItems.slice(-1) : nextItems;

      setSelectedItems(usedNextItems);

      if (onChange) {
        onChange(usedNextItems);
      }
    };
  }, [as, onChange]);

  const hasControlledItems = items !== undefined && loadItemsProp === undefined;
  const sourceItems = hasControlledItems ? items : itemsList;
  const filteredItems = useMemo(() => {
    if (!inputValue) {
      return sourceItems;
    }

    return sourceItems.filter(item =>
      fuzzyMatch(itemToString(item), inputValue)
    );
  }, [inputValue, sourceItems, itemToString]);

  const loadItems = useMemo(() => {
    return loadItemsProp
      ? async (inputValue: string) => {
          setInternalIsLoading(true);
          try {
            const {items: loadedItems, hasMore} =
              await loadItemsProp(inputValue);
            return {items: loadedItems, hasMore};
          } finally {
            setInternalIsLoading(false);
          }
        }
      : undefined;
  }, [loadItemsProp]);

  const loadInitialItems = useCallback(async () => {
    if (loadItems) {
      hasAttemptedInitialLoadRef.current = true;
      const {items: loadedItems, hasMore: nextHasMore} =
        await loadItems(inputValue);
      initialLoadedItems.current = loadedItems;
      setItemsList(loadedItems);
      setHasMore(nextHasMore);
    }
  }, [loadItems, inputValue]);

  const {getSelectedItemProps, removeSelectedItem, getDropdownProps} =
    useMultipleSelection({
      selectedItems,
      onStateChange: useCallback(
        (changes: UseMultipleSelectionStateChange<T>) => {
          switch (changes.type) {
            case useMultipleSelection.stateChangeTypes
              .FunctionRemoveSelectedItem:
              doSelectedItems(changes.selectedItems ?? []);
              break;
            default:
              break;
          }
        },
        [doSelectedItems]
      ),
    });

  const {
    isOpen,
    toggleMenu,
    openMenu,
    closeMenu,
    getMenuProps,
    getInputProps,
    getLabelProps,
    getItemProps,
    getToggleButtonProps,
  } = useCombobox({
    inputValue,
    itemToString,
    selectedItem: null,
    items: customItemsList.concat(filteredItems),
    ...(isItemDisabled && {isItemDisabled}),
    stateReducer: (_state, actionAndChanges) => {
      const {changes, type} = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.InputClick:
        case useCombobox.stateChangeTypes.ItemClick: {
          return {
            ...changes,
            isOpen:
              as === 'ComboBox' &&
              type === useCombobox.stateChangeTypes.ItemClick
                ? false
                : true,
          };
        }
        default:
          return changes;
      }
    },
    onStateChange: useCallback(
      ({type, selectedItem}: UseComboboxStateChange<T>) => {
        switch (type) {
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.ItemClick:
            if (selectedItem) {
              const isRemovingItem = selectedItems.some(
                item => itemToString(item) === itemToString(selectedItem)
              );

              if (isRemovingItem && !clearable && selectedItems.length === 1) {
                return;
              }

              const nextSelectedItems = isRemovingItem
                ? selectedItems.filter(
                    item => itemToString(item) !== itemToString(selectedItem)
                  )
                : Array.from(new Set([...selectedItems, selectedItem]));

              doSelectedItems(nextSelectedItems);
            }
            break;
          default:
            break;
        }
      },
      [doSelectedItems, itemToString, selectedItems, clearable]
    ),
  });

  const handleOnSearchKeyDown = useCallback(
    (evt: KeyboardEvent<HTMLInputElement>) => {
      if (evt.key === 'Enter') {
        const item = sourceItems.find(
          item => itemToString(item) === inputValue
        );

        // Block disabled items from keyboard selection
        if (item && isItemDisabled?.(item)) {
          return;
        }

        const nextItem = item
          ? item
          : allowCustomItems
            ? (inputValue as unknown as T)
            : null;

        evt.preventDefault();

        if (nextItem) {
          const nextSelectedItems = Array.from(
            new Set([...selectedItems, nextItem])
          );

          if (!item) {
            customItems.current.add(inputValue as unknown as T);
          }

          doSelectedItems(nextSelectedItems);
          setInputValue('');
        }
      }
    },
    [
      sourceItems,
      inputValue,
      selectedItems,
      allowCustomItems,
      doSelectedItems,
      itemToString,
      isItemDisabled,
    ]
  );

  const resolvedIsLoading = loadItemsProp
    ? internalIsLoading
    : typeof isLoadingProp === 'boolean'
      ? isLoadingProp
      : internalIsLoading;

  const contextValue = useMemo<MultiComboBoxContextType<T>>(() => {
    return {
      as,
      clearable,
      isOpen,
      isLoading: resolvedIsLoading,
      hasMore,
      setHasMore,
      itemsListRef,
      anchorRef,
      inputValue,
      filteredItems,
      selectedItems,
      customItemsList,
      loadItems,
      itemToString,
      isItemDisabled,
      setSelectedItems,
      setItemsList,
      setInputValue,
      handleOnSearchKeyDown,
      toggleMenu,
      openMenu,
      closeMenu,
      removeSelectedItem,
      getMenuProps,
      getInputProps,
      getLabelProps,
      getItemProps,
      getDropdownProps,
      getToggleButtonProps,
      getSelectedItemProps,
      registerInfiniteLoader,
      infiniteLoaderRef,
      hasExternalInfiniteLoader,
      anchorPlaceholder,
      toggleAriaLabel,
      emptyStateMessage,
    };
  }, [
    as,
    clearable,
    isOpen,
    resolvedIsLoading,
    hasMore,
    setHasMore,
    itemsListRef,
    anchorRef,
    inputValue,
    filteredItems,
    selectedItems,
    customItemsList,
    loadItems,
    itemToString,
    isItemDisabled,
    setInputValue,
    setItemsList,
    setSelectedItems,
    handleOnSearchKeyDown,
    toggleMenu,
    openMenu,
    closeMenu,
    removeSelectedItem,
    getMenuProps,
    getInputProps,
    getLabelProps,
    getItemProps,
    getDropdownProps,
    getToggleButtonProps,
    getSelectedItemProps,
    registerInfiniteLoader,
    infiniteLoaderRef,
    hasExternalInfiniteLoader,
    anchorPlaceholder,
    toggleAriaLabel,
    emptyStateMessage,
  ]);

  const prevItemsRef = useRef(items);

  // React to prop changes during rendering.
  // Keep controlled mode stable when item content hasn't actually changed.
  // `loadItems` owns the list; ignore `items` so the two modes cannot mix.
  if (!loadItemsProp && prevItemsRef.current !== items) {
    const prevItems = prevItemsRef.current;
    const nextItems = items;

    prevItemsRef.current = nextItems;

    if (nextItems !== undefined) {
      const isChangedItems = !checkEquality(nextItems, prevItems ?? []);
      if (isChangedItems) {
        setItemsList(nextItems);
        setSelectedItems(initialSelectedItems ?? []);
        initialLoadedItems.current = [];
        hasAttemptedInitialLoadRef.current = false;
      }
    } else if (prevItems !== undefined) {
      // Preserve the last controlled snapshot when switching to uncontrolled mode.
      setItemsList(prevItems);
      initialLoadedItems.current = [];
      hasAttemptedInitialLoadRef.current = false;
    }
  }

  if (
    loadItems &&
    !hasAttemptedInitialLoadRef.current &&
    !initialLoadedItems.current.length &&
    !internalIsLoading
  ) {
    loadInitialItems();
  }

  if (!allowCustomItems && customItems.current.size > 0) {
    doSelectedItems(
      selectedItems.filter(item => !customItems.current.has(item))
    );
    customItems.current.clear();
  }

  if (as === 'ComboBox' && selectedItems.length > 1) {
    doSelectedItems(selectedItems.slice(-1));
  }

  return (
    <MultiComboBoxContext value={contextValue}>
      {asChild ? (
        <Flex direction="column">{children}</Flex>
      ) : (
        <>
          <MultiComboBox.Anchor />
          <MultiComboBox.Surface>
            <MultiComboBox.Items />
          </MultiComboBox.Surface>
        </>
      )}
    </MultiComboBoxContext>
  );
};
Root.displayName = 'Root';

const MultiComboBox = Object.assign(Root, {
  Anchor,
  SelectedItems,
  ToggleButton,
  Surface,
  SurfaceLoadingIndicator,
  Search,
  Items,
});

export type {
  LoadItems,
  MultiComboBoxItemsProps,
  MultiComboBoxLoadItemsProps,
  MultiComboBoxProps,
};
export {MultiComboBox};
export {useMultiComboBoxContext} from '../context.js';
